/**
 * PdfDownloadUtils Module
 * 
 * Utility functions for generating PDF files from construction worker payslip tables.
 * Handles complex PDF generation with proper styling, Japanese font rendering,
 * and optimization for mobile devices.
 * 
 * Features:
 * - WYSIWYG PDF generation using html2pdf.js
 * - Custom color application for payslip sections
 * - Japanese font rendering optimization
 * - Mobile device compatibility
 * - A4 landscape format optimization
 * - Row height consistency enforcement
 * 
 * @module PdfDownloadUtils
 */

import html2pdf from 'html2pdf.js';
import { ConstructionPayslipSettings } from './SettingsUtils';

/** A4 landscape content box used to cap html2canvas capture on mobile (96 DPI, with margin). */
const MOBILE_PDF_CAPTURE_WIDTH_PX = 1100;
const MOBILE_PDF_CAPTURE_HEIGHT_PX = 790;
/** html2pdf margin [top, right, bottom, left] mm — tight bottom so 会社名 is not clipped. */
const MOBILE_PDF_MARGIN_MM: [number, number, number, number] = [3, 5, 1, 5];

const isMobilePdfExportViewport = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

/**
 * Handles PDF download for construction worker payslip.
 * 
 * Generates a PDF file from the payslip table with proper styling, colors,
 * and formatting. Applies custom settings for borders, backgrounds, and fonts.
 * Optimizes for Japanese character rendering on mobile devices.
 * 
 * Process:
 * 1. Validates input parameters
 * 2. Stores original CSS custom properties
 * 3. Applies custom colors to table cells
 * 4. Clones table element for PDF generation
 * 5. Applies PDF-specific styling (scaling, fonts, layout)
 * 6. Generates PDF using html2pdf.js
 * 7. Cleans up temporary elements and restores original styles
 * 
 * @param tableRef - React ref to the table element to convert to PDF
 * @param selectedYear - Selected year for payslip (e.g., '2024')
 * @param selectedMonth - Selected month for salary payslip (e.g., '12')
 * @param empCode - Employee name for filename
 * @param selectedSite - Construction site name for filename
 * @param payslipSettings - Settings object containing colors, fonts, and display options
 * @param payslipKind - 'salary' or 'bonus'
 * @param selectedPeriod - Selected bonus period when payslipKind is 'bonus' (e.g., '7')
 * @returns void
 * 
 * @example
 * ```ts
 * handlePdfDownload(
 *   tableRef,
 *   '2024',
 *   '12',
 *   'EMP001',
 *   'Site A',
 *   payslipSettings
 * );
 * ```
 */
export const handlePdfDownload = (
  tableRef: React.RefObject<HTMLDivElement | null>,
  selectedYear: string,
  selectedMonth: string,
  empCode: string,
  selectedSite: string,
  payslipSettings: ConstructionPayslipSettings,
  payslipKind: 'salary' | 'bonus' = 'salary',
  selectedPeriod?: string
): void => {
  
  // Validate that table element exists
  if (!tableRef.current) {
    alert('テーブルが表示されていません。まず明細書を生成してください。');
    return;
  }

  const timeValue = payslipKind === 'bonus' ? selectedPeriod : selectedMonth;
  if (
    !selectedYear ||
    !timeValue ||
    !empCode ||
    !selectedSite
  ) {
    alert(
      payslipKind === 'bonus'
        ? '年、月、名前を正しく入力し、賞与データを表示してからダウンロードしてください。'
        : '現場、年、月、名前を正しく入力してください。'
    );
    return;
  }

  // Get the table element from ref
  const element = tableRef.current;
  const isMobilePdfExport = isMobilePdfExportViewport();
  
  /**
   * Store original CSS custom properties before applying PDF-specific styles.
   * These will be restored after PDF generation to maintain original appearance.
   */
  const originalBorderColor = element.style.getPropertyValue('--payslip-border-color');
  const originalBackgroundColor = element.style.getPropertyValue('--payslip-background-color');
  const originalRoumuhiBorderColor = element.style.getPropertyValue('--payslip-roumuhi-border-color');
  const originalRoumuhiBackgroundColor = element.style.getPropertyValue('--payslip-roumuhi-background-color');
  
  /**
   * Applies custom colors from payslip settings to the table element.
   * 
   * Sets CSS custom properties and inline styles on all table cells to ensure
   * colors are visible in PDF output. Handles different cell types:
   * - Roumuhi (labor cost) section cells
   * - Green-row cells
   * - White-row cells
   * - Regular cells
   * 
   * Also handles thick borders and merged cells (rowSpan) for proper PDF rendering.
   * 
   * @param el - HTML element to apply colors to
   * 
   * @private
   */
  const applyCustomColors = (el: HTMLElement) => {
    // Extract color values from settings
    const borderColor = payslipSettings.borderColor;
    const backgroundColor = payslipSettings.backgroundColor;
    const roumuhiBorderColor = payslipSettings.roumuhiBorderColor;
    const roumuhiBackgroundColor = payslipSettings.roumuhiBackgroundColor;
    
    // Set CSS custom properties on the root element for CSS variable inheritance
    el.style.setProperty('--payslip-border-color', borderColor);
    el.style.setProperty('--payslip-background-color', backgroundColor);
    el.style.setProperty('--payslip-roumuhi-border-color', roumuhiBorderColor);
    el.style.setProperty('--payslip-roumuhi-background-color', roumuhiBackgroundColor);
    
    /**
     * Apply direct inline styles to all table cells to ensure borders and colors
     * are visible in PDF output. html2pdf.js may not render CSS custom properties
     * correctly, so inline styles are required.
     */
    const allTableCells = el.querySelectorAll('td, th');
    allTableCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      
      // Apply border color to all cells
      htmlCell.style.setProperty('border-color', borderColor);
      
      /**
       * Apply background color based on cell type and section:
       * - Roumuhi section: Uses roumuhi colors (orange theme)
       * - Green rows: Uses main table background color (light green)
       * - White rows/separators: White background
       * - Default: Main table background color
       */
      if (htmlCell.closest('.construction-roumuhi-section')) {
        // Roumuhi (labor cost) section cells - orange theme
        htmlCell.style.setProperty('background-color', roumuhiBackgroundColor);
        htmlCell.style.setProperty('border-color', roumuhiBorderColor);
      } else if (htmlCell.closest('.green-row')) {
        // Green row cells - light green background
        htmlCell.style.setProperty('background-color', backgroundColor);
        htmlCell.style.setProperty('border-color', borderColor);
      } else if (htmlCell.closest('.white-row') || htmlCell.closest('.construction-no-name-section') || htmlCell.closest('.construction-separator-row')) {
        // White rows and separator rows - white background
        htmlCell.style.setProperty('background-color', '#ffffff');
        htmlCell.style.setProperty('border-color', borderColor);
      } else {
        // Default cells - main table background color
        htmlCell.style.setProperty('background-color', backgroundColor);
        htmlCell.style.setProperty('border-color', borderColor);
      }
      
      /**
       * Ensure borders are visible and sharp in PDF.
       * Sets border style to solid and width to 1px for consistent rendering.
       * Uses border-box sizing for better border rendering.
       */
      const computedStyle = window.getComputedStyle(htmlCell);
      if (computedStyle.borderStyle !== 'none') {
        htmlCell.style.setProperty('border-style', 'solid');
        htmlCell.style.setProperty('border-width', '1px');
        // Use border-box for better border rendering in PDF
        htmlCell.style.setProperty('box-sizing', 'border-box');
      }
      
      /**
       * Handle thick borders for special cells.
       * These are used for outer borders and section dividers.
       */
      if (htmlCell.classList.contains('construction-thick-border-left')) {
        htmlCell.style.setProperty('border-left-width', '2px');
      }
      if (htmlCell.classList.contains('construction-thick-border-right')) {
        htmlCell.style.setProperty('border-right-width', '2px');
      }
      if (htmlCell.classList.contains('construction-thick-border-top')) {
        htmlCell.style.setProperty('border-top-width', '2px');
      }
      if (htmlCell.classList.contains('construction-thick-border-bottom')) {
        htmlCell.style.setProperty('border-bottom-width', '2px');
      }
      /**
       * No top border (same as 控除 section) so inner horizontal lines are single, not doubled.
       */
      if (htmlCell.classList.contains('construction-no-top-border')) {
        htmlCell.style.setProperty('border-top-width', '0');
      }
      /**
       * No right border on first column so inner vertical line is single (1px from next cell's left), not thick.
       */
      if (htmlCell.classList.contains('construction-no-right-border')) {
        htmlCell.style.setProperty('border-right-width', '0');
      }
    });

    /**
     * Spacer row between company name and memo: no border, white background.
     */
    const spacerCells = el.querySelectorAll('.construction-memo-spacer-cell');
    spacerCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      htmlCell.style.setProperty('border', 'none');
      htmlCell.style.setProperty('background-color', '#ffffff');
    });
    
    /**
     * Special handling for merged cells (cells with rowSpan attribute).
     * Ensures proper rendering of vertically merged cells in PDF output.
     */
    const mergedCells = el.querySelectorAll('td[rowspan], th[rowspan]');
    mergedCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      const rowSpan = parseInt(htmlCell.getAttribute('rowspan') || '1');
      
      /**
       * Apply comprehensive styling to merged cells for PDF rendering:
       * - Background and border colors
       * - Border style and width
       * - Text alignment and vertical centering
       * - Font styling for better readability
       */
      htmlCell.style.setProperty('background-color', backgroundColor);
      htmlCell.style.setProperty('border-color', borderColor);
      htmlCell.style.setProperty('border-style', 'solid');
      htmlCell.style.setProperty('border-width', '1px');
      htmlCell.style.setProperty('vertical-align', 'middle');
      htmlCell.style.setProperty('text-align', 'center');
      htmlCell.style.setProperty('box-sizing', 'border-box');
      htmlCell.style.setProperty('color', '#000000');              // Pure black text
      htmlCell.style.setProperty('font-weight', 'bold');           // Bold text for visibility
      htmlCell.style.setProperty('text-rendering', 'optimizeLegibility');
      
      /**
       * Handle thick borders for merged cells (used for section dividers).
       */
      if (htmlCell.classList.contains('construction-thick-border-left')) {
        htmlCell.style.setProperty('border-left-width', '2px');
      }
      if (htmlCell.classList.contains('construction-thick-border-right')) {
        htmlCell.style.setProperty('border-right-width', '2px');
      }
      if (htmlCell.classList.contains('construction-thick-border-top')) {
        htmlCell.style.setProperty('border-top-width', '2px');
      }
      if (htmlCell.classList.contains('construction-thick-border-bottom')) {
        htmlCell.style.setProperty('border-bottom-width', '2px');
      }
      
      /**
       * Special handling for vertical text cells (used for section labels).
       * Calculates height based on rowSpan to ensure proper vertical text display.
       */
      if (htmlCell.classList.contains('construction-vertical-text')) {
        htmlCell.style.setProperty('writing-mode', 'vertical-rl');  // Vertical right-to-left
        htmlCell.style.setProperty('text-orientation', 'mixed');
        htmlCell.style.setProperty('transform', 'rotate(0deg)');
        htmlCell.style.setProperty('white-space', 'nowrap');
        htmlCell.style.setProperty('overflow', 'hidden');
        htmlCell.style.setProperty('text-overflow', 'ellipsis');
        // Height calculated based on number of rows spanned
        htmlCell.style.setProperty('min-height', `${rowSpan * 20}px`);
        htmlCell.style.setProperty('height', `${rowSpan * 20}px`);
      }
      
    });
    
    // Also apply CSS custom properties to all elements
    const allElements = el.querySelectorAll('*');
    allElements.forEach((elem: Element) => {
      const htmlElem = elem as HTMLElement;
      htmlElem.style.setProperty('--payslip-border-color', borderColor);
      htmlElem.style.setProperty('--payslip-background-color', backgroundColor);
      htmlElem.style.setProperty('--payslip-roumuhi-border-color', roumuhiBorderColor);
      htmlElem.style.setProperty('--payslip-roumuhi-background-color', roumuhiBackgroundColor);
    });
    
  };
  
  // Apply custom colors to the original element
  applyCustomColors(element);
  
  /**
   * Create a temporary container for PDF generation.
   * The container is positioned off-screen to avoid visual disruption during PDF generation.
   * Uses fixed positioning and negative coordinates to hide it from view.
   */
  const tempContainer = document.createElement('div');
  tempContainer.style.cssText = `
    position: fixed;
    top: -9999px;      /* Position off-screen */
    left: -9999px;     /* Position off-screen */
    width: 1123px;     /* A4 landscape width at 96 DPI */
    height: 794px;     /* A4 landscape height at 96 DPI */
    overflow: hidden;
    background: white;
    z-index: -1;       /* Behind all content */
  `;
  
  /**
   * Clone the table element for PDF generation.
   * Cloning preserves desktop dimensions and allows applying PDF-specific styling
   * without affecting the original displayed table.
   */
  const clonedElement = element.cloneNode(true) as HTMLElement;
  clonedElement.style.cssText = isMobilePdfExport
    ? `
    width: auto;
    max-width: none;
    height: auto;
    max-height: none;
    overflow: visible;
    transform: none;
    transform-origin: top left;
  `
    : `
    width: auto;
    max-width: none;
    height: auto;
    max-height: none;
    overflow: visible;
    transform: scale(0.9);
    transform-origin: top left;
  `;
  
  // Add PDF-specific class to cloned element only
  clonedElement.classList.add('pdf-generation');
  
  // Apply desktop layout to cloned element's containers
  const clonedContainer = clonedElement.closest('.construction-table-area') as HTMLElement;
  const clonedPayslipContainer = clonedElement.closest('.construction-payslip-container') as HTMLElement;
  const clonedScaleWrapper = clonedElement.closest('.payslip-scale-wrapper') as HTMLElement;
  
  if (clonedContainer) {
    clonedContainer.style.padding = '10px';
    clonedContainer.style.overflowX = 'visible';
    clonedContainer.style.overflowY = 'visible';
    clonedContainer.style.minWidth = 'auto';
    clonedContainer.style.maxWidth = 'none';
  }
  
  if (clonedPayslipContainer) {
    clonedPayslipContainer.style.padding = '10px';
    clonedPayslipContainer.style.overflowX = 'visible';
    clonedPayslipContainer.style.overflowY = 'visible';
    clonedPayslipContainer.style.minWidth = 'auto';
    clonedPayslipContainer.style.maxWidth = 'none';
  }
  
  if (clonedScaleWrapper) {
    clonedScaleWrapper.style.setProperty('--scale', '1');
  }
  
  tempContainer.appendChild(clonedElement);
  document.body.appendChild(tempContainer);
  
  // Store reference for cleanup
  (element as any).tempContainer = tempContainer;
  (element as any).clonedElement = clonedElement;
  
  /**
   * Applies PDF-specific styling to ensure proper A4 landscape format fitting.
   * 
   * Optimizes the table for PDF generation by:
   * - Setting dimensions for A4 landscape (297mm x 210mm = 1123px x 794px at 96 DPI)
   * - Scaling content to fit on one page
   * - Optimizing font rendering for Japanese characters
   * - Ensuring row heights are consistent
   * - Applying Japanese font stack to prevent character corruption on mobile devices
   * 
   * @param el - HTML element to apply PDF styling to
   * 
   * @private
   */
  const applyPdfStyling = (el: HTMLElement) => {
    // Add PDF-specific class for CSS targeting
    el.classList.add('pdf-generation');
    
    /**
     * Set container dimensions optimized for A4 landscape format.
     * A4 landscape dimensions: 297mm x 210mm
     * At 96 DPI: approximately 1123px x 794px
     * Slightly reduced to ensure content fits on one page with margins.
     */
    el.style.width = '1100px';       // Slightly reduced from 1123px
    el.style.maxWidth = '1100px';
    el.style.maxHeight = isMobilePdfExport ? 'none' : '750px';
    el.style.overflow = 'visible';
    el.style.margin = '0 auto';
    el.style.display = 'block';
    el.style.transformOrigin = 'top left';
    el.style.transform = isMobilePdfExport ? 'none' : 'scale(0.8)'; // Mobile: avoid transform-inflated scrollHeight
    
    /**
     * Enhanced rendering quality settings for crisp PDF output.
     * These settings improve text and image rendering quality in the generated PDF.
     */
    el.style.imageRendering = 'crisp-edges';                    // Sharp image rendering
    el.style.textRendering = 'optimizeLegibility';              // Better text rendering
    (el.style as any).fontSmoothing = 'antialiased';            // Better font smoothing
    (el.style as any).webkitFontSmoothing = 'antialiased';      // WebKit font smoothing
    (el.style as any).mozOsxFontSmoothing = 'grayscale';        // Firefox font smoothing
    
    /**
     * Forces explicit Japanese font-family for all text elements.
     * This is critical for mobile devices (like AQUOSsense6) that may have
     * font rendering issues with Japanese characters in PDF output.
     * 
     * @param element - HTML element to apply Japanese font to
     */
    const forceJapaneseFont = (element: HTMLElement) => {
      // Japanese font stack prioritized for best character rendering
      const japaneseFontStack = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
      element.style.setProperty('font-family', japaneseFontStack, 'important');
      element.style.setProperty('unicode-bidi', 'embed', 'important');
      element.style.setProperty('direction', 'ltr', 'important');
    };
    
    /**
     * Apply Japanese font to all text-containing elements.
     * Ensures proper Japanese character rendering throughout the payslip.
     */
    const allTextElements = el.querySelectorAll('td, th, div, span, p');
    allTextElements.forEach((elem: Element) => {
      const htmlElem = elem as HTMLElement;
      if (htmlElem.textContent && htmlElem.textContent.trim() !== '') {
        forceJapaneseFont(htmlElem);
      }
    });
    
    /**
     * Checks if a table cell is truly empty (no content).
     * 
     * A cell is considered empty if:
     * - textContent is empty or whitespace only
     * - innerHTML is empty or only contains whitespace/br tags
     * 
     * Note: &nbsp; (non-breaking space) counts as content, so cells with &nbsp;
     * are NOT considered empty (important for maintaining row height).
     * 
     * @param cell - HTML cell element to check
     * @returns true if cell is empty, false otherwise
     */
    const isEmptyCell = (cell: HTMLElement): boolean => {
      const text = cell.textContent || '';
      const innerHTML = (cell.innerHTML || '').trim();
      // Cell is empty if text is empty AND innerHTML is empty or only contains whitespace/br tags
      // Note: &nbsp; counts as content, so if innerHTML is &nbsp;, cell is NOT empty
      return text.trim() === '' && 
             (innerHTML === '' || 
              innerHTML === '<br>' || 
              innerHTML === '<br/>' ||
              innerHTML === '<br />');
    };
    
    /**
     * Ensures a cell has content to maintain consistent row height in PDF.
     * 
     * Adds a non-breaking space (&nbsp;) to empty cells. This is critical for
     * maintaining consistent row heights in PDF output, as empty cells may
     * collapse in some PDF rendering scenarios.
     * 
     * @param cell - HTML cell element to ensure content for
     */
    const ensureCellContent = (cell: HTMLElement): void => {
      if (isEmptyCell(cell)) {
        // Add non-breaking space to maintain row height
        cell.innerHTML = '&nbsp;';
      }
    };

    const applyEmployeeNameCellPdfStyle = (htmlCell: HTMLElement): void => {
      const text = (htmlCell.textContent || '').trim();
      const len = text.length;
      let fontSize = '11px';
      if (len > 14) fontSize = '8px';
      else if (len > 10) fontSize = '9px';
      else if (len > 7) fontSize = '10px';

      htmlCell.style.setProperty('height', 'auto', 'important');
      htmlCell.style.setProperty('min-height', '26px', 'important');
      htmlCell.style.setProperty('max-height', 'none', 'important');
      htmlCell.style.setProperty('white-space', 'normal', 'important');
      htmlCell.style.setProperty('overflow', 'visible', 'important');
      htmlCell.style.setProperty('line-height', '1.1', 'important');
      htmlCell.style.setProperty('vertical-align', 'middle', 'important');
      htmlCell.style.setProperty('word-break', 'break-all', 'important');
      htmlCell.style.setProperty('overflow-wrap', 'anywhere', 'important');
      htmlCell.style.setProperty('text-align', 'center', 'important');

      const nameSpan = htmlCell.querySelector('.construction-employee-name-text') as HTMLElement | null;
      if (nameSpan) {
        nameSpan.style.setProperty('font-size', fontSize, 'important');
        nameSpan.style.setProperty('white-space', 'normal', 'important');
        nameSpan.style.setProperty('line-height', '1.1', 'important');
      } else {
        htmlCell.style.setProperty('font-size', fontSize, 'important');
      }
    };
    
    // First pass: Ensure ALL empty cells in roumohi and koujo sections have content
    const roumohiTable = el.querySelector('.construction-roumuhi-table');
    if (roumohiTable) {
      const roumohiCells = roumohiTable.querySelectorAll('td, th');
      roumohiCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        ensureCellContent(htmlCell);
      });
    }
    
    const koujoRows = el.querySelectorAll('.construction-koujo-section');
    koujoRows.forEach((row: Element) => {
      const cells = row.querySelectorAll('td, th');
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        ensureCellContent(htmlCell);
      });
    });
    
    // Second pass: Apply consistent styling to all cells
    const allCells = el.querySelectorAll('td, th');
    allCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;

      if (htmlCell.classList.contains('construction-employee-name-cell')) {
        applyEmployeeNameCellPdfStyle(htmlCell);
        return;
      }
      
      /**
       * Special handling for 振込 (Transfer) section.
       * Forces consistent row height matching other sections (19px) for uniform appearance.
       */
      if (htmlCell.closest('.construction-furikomi-section')) {
        ensureCellContent(htmlCell);
        // Force consistent height: 19px total (13px content + 3px top + 3px bottom padding)
        htmlCell.style.setProperty('min-height', '19px', 'important');
        htmlCell.style.setProperty('height', '19px', 'important');
        htmlCell.style.setProperty('max-height', '19px', 'important');
        htmlCell.style.setProperty('line-height', '13px', 'important'); // Content area height
        htmlCell.style.setProperty('padding-top', '3px', 'important');
        htmlCell.style.setProperty('padding-bottom', '3px', 'important');
        htmlCell.style.setProperty('padding-left', '4px', 'important');
        htmlCell.style.setProperty('padding-right', '4px', 'important');
        htmlCell.style.setProperty('vertical-align', 'middle', 'important');
        htmlCell.style.setProperty('box-sizing', 'border-box', 'important');
        htmlCell.style.setProperty('display', 'table-cell', 'important');
        htmlCell.style.setProperty('overflow', 'hidden', 'important');
        htmlCell.style.setProperty('font-size', '10px', 'important');
        htmlCell.style.setProperty('font-weight', 'bold', 'important');
        htmlCell.style.setProperty('color', '#000000', 'important');
        htmlCell.style.setProperty('text-rendering', 'optimizeLegibility', 'important');
        // Force Japanese font to prevent character corruption on mobile devices
        const japaneseFontStack = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
        htmlCell.style.setProperty('font-family', japaneseFontStack, 'important');
        return; // Skip generic styling for this section
      }
      
      /**
       * Special handling for roumuhi (労務費明細書 - Labor Cost Detail) section.
       * Ensures consistent row height for the daily attendance table.
       */
      const roumohiRow = htmlCell.closest('.construction-roumuhi-section');
      const isRoumohiTable = htmlCell.closest('.construction-roumuhi-table');
      if (roumohiRow || isRoumohiTable) {
        ensureCellContent(htmlCell);
        // Force consistent height: 19px total (13px content + 3px top + 3px bottom padding)
        htmlCell.style.setProperty('min-height', '19px', 'important');
        htmlCell.style.setProperty('height', '19px', 'important');
        htmlCell.style.setProperty('max-height', '19px', 'important');
        htmlCell.style.setProperty('line-height', '13px', 'important');
        htmlCell.style.setProperty('padding', '3px 4px', 'important');
        htmlCell.style.setProperty('font-size', '10px', 'important');
        htmlCell.style.setProperty('vertical-align', 'middle', 'important');
        htmlCell.style.setProperty('font-weight', 'bold', 'important');
        htmlCell.style.setProperty('color', '#000000', 'important');
        htmlCell.style.setProperty('text-rendering', 'optimizeLegibility', 'important');
        htmlCell.style.setProperty('box-sizing', 'border-box', 'important');
        htmlCell.style.setProperty('display', 'table-cell', 'important');
        htmlCell.style.setProperty('overflow', 'hidden', 'important');
        htmlCell.style.setProperty('white-space', 'nowrap', 'important');
        // Force Japanese font to prevent character corruption on mobile devices
        const japaneseFontStackRoumohi = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
        htmlCell.style.setProperty('font-family', japaneseFontStackRoumohi, 'important');
        return; // Skip generic styling for this section
      }
      
      /**
       * Special handling for koujo (控除 - Deduction) section.
       * Ensures consistent row height matching main table styling.
       */
      const koujoRow = htmlCell.closest('.construction-koujo-section');
      if (koujoRow) {
        ensureCellContent(htmlCell);
        // Force consistent height: 19px total (13px content + 3px top + 3px bottom padding)
        htmlCell.style.setProperty('min-height', '19px', 'important');
        htmlCell.style.setProperty('height', '19px', 'important');
        htmlCell.style.setProperty('max-height', '19px', 'important');
        htmlCell.style.setProperty('line-height', '13px', 'important');
        htmlCell.style.setProperty('padding', '3px 4px', 'important');
        htmlCell.style.setProperty('font-size', '10px', 'important');
        htmlCell.style.setProperty('vertical-align', 'middle', 'important');
        htmlCell.style.setProperty('font-weight', 'bold', 'important');
        htmlCell.style.setProperty('color', '#000000', 'important');
        htmlCell.style.setProperty('text-rendering', 'optimizeLegibility', 'important');
        htmlCell.style.setProperty('box-sizing', 'border-box', 'important');
        htmlCell.style.setProperty('display', 'table-cell', 'important');
        htmlCell.style.setProperty('overflow', 'hidden', 'important');
        htmlCell.style.setProperty('white-space', 'nowrap', 'important');
        // Force Japanese font to prevent character corruption on mobile devices
        const japaneseFontStackKoujo = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
        htmlCell.style.setProperty('font-family', japaneseFontStackKoujo, 'important');
        return; // Skip generic styling for this section
      }
      
      /**
       * Generic styling for other cells - more compact for single-page PDF.
       * Optimized dimensions to fit all content on one A4 landscape page.
       */
      ensureCellContent(htmlCell);
      htmlCell.style.minHeight = '15px';
      htmlCell.style.height = '15px';
      htmlCell.style.maxHeight = '15px';
      htmlCell.style.padding = '1px 2px';
      htmlCell.style.fontSize = '8px';
      htmlCell.style.lineHeight = '1.2';
      htmlCell.style.fontWeight = 'bold';
      htmlCell.style.color = '#000000';
      htmlCell.style.textRendering = 'optimizeLegibility';
      // Force Japanese font to prevent character corruption on mobile devices
      const japaneseFontStack = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
      htmlCell.style.setProperty('font-family', japaneseFontStack, 'important');
    });
    
    /**
     * Third pass: Force consistent row heights for roumuhi and koujo sections.
     * 
     * Processes rows AFTER cells to ensure all cells in each row have proper height.
     * This is critical for rows where all cells are empty, as empty rows may
     * collapse in PDF output without explicit height enforcement.
     */
    const allRoumohiRows = el.querySelectorAll('.construction-roumuhi-section');
    allRoumohiRows.forEach((row: Element) => {
      const htmlRow = row as HTMLElement;
      const cells = htmlRow.querySelectorAll('td, th');
      
      /**
       * Ensure ALL cells in this row have content - critical for empty rows.
       * Applies critical styles with important flag to override any CSS rules.
       */
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        ensureCellContent(htmlCell);
        // Apply critical styles with important flag to override any CSS
        htmlCell.style.setProperty('min-height', '19px', 'important');
        htmlCell.style.setProperty('height', '19px', 'important');
        htmlCell.style.setProperty('max-height', '19px', 'important');
        htmlCell.style.setProperty('line-height', '13px', 'important');
        htmlCell.style.setProperty('vertical-align', 'middle', 'important');
        htmlCell.style.setProperty('padding-top', '3px', 'important');
        htmlCell.style.setProperty('padding-bottom', '3px', 'important');
        htmlCell.style.setProperty('padding-left', '4px', 'important');
        htmlCell.style.setProperty('padding-right', '4px', 'important');
        htmlCell.style.setProperty('box-sizing', 'border-box', 'important');
        htmlCell.style.setProperty('display', 'table-cell', 'important');
        htmlCell.style.setProperty('overflow', 'hidden', 'important');
        // Force content height calculation for empty cells
        if (htmlCell.textContent?.trim() === '' && htmlCell.innerHTML.trim() === '') {
          htmlCell.innerHTML = '&nbsp;';
        }
      });
      
      /**
       * Force row height explicitly with important flag.
       * Ensures the entire row maintains 19px height even if all cells are empty.
       */
      htmlRow.style.setProperty('display', 'table-row', 'important');
      htmlRow.style.setProperty('min-height', '19px', 'important');
      htmlRow.style.setProperty('height', '19px', 'important');
      htmlRow.style.setProperty('max-height', '19px', 'important');
    });
    
    const allKoujoRows = el.querySelectorAll('.construction-koujo-section');
    allKoujoRows.forEach((row: Element) => {
      const htmlRow = row as HTMLElement;
      const cells = htmlRow.querySelectorAll('td, th');
      
      // Ensure ALL cells in this row have content - critical for empty rows
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        ensureCellContent(htmlCell);
        // Apply critical styles with important flag to override any CSS
        htmlCell.style.setProperty('min-height', '19px', 'important');
        htmlCell.style.setProperty('height', '19px', 'important');
        htmlCell.style.setProperty('max-height', '19px', 'important');
        htmlCell.style.setProperty('line-height', '13px', 'important');
        htmlCell.style.setProperty('vertical-align', 'middle', 'important');
        htmlCell.style.setProperty('padding-top', '3px', 'important');
        htmlCell.style.setProperty('padding-bottom', '3px', 'important');
        htmlCell.style.setProperty('padding-left', '4px', 'important');
        htmlCell.style.setProperty('padding-right', '4px', 'important');
        htmlCell.style.setProperty('box-sizing', 'border-box', 'important');
        htmlCell.style.setProperty('display', 'table-cell', 'important');
        htmlCell.style.setProperty('overflow', 'hidden', 'important');
        // Force content height calculation
        if (htmlCell.textContent?.trim() === '' && htmlCell.innerHTML.trim() === '') {
          htmlCell.innerHTML = '&nbsp;';
        }
      });
      
      // Force row height explicitly with important flag
      htmlRow.style.setProperty('display', 'table-row', 'important');
      htmlRow.style.setProperty('min-height', '19px', 'important');
      htmlRow.style.setProperty('height', '19px', 'important');
      htmlRow.style.setProperty('max-height', '19px', 'important');
    });
    
    // Fourth pass: Ensure furikomi section rows have consistent heights
    const allFurikomiRows = el.querySelectorAll('.construction-furikomi-section');
    allFurikomiRows.forEach((row: Element) => {
      const htmlRow = row as HTMLElement;
      const cells = htmlRow.querySelectorAll('td, th');
      
      // Ensure ALL cells in this row have content
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        ensureCellContent(htmlCell);
        // Apply critical styles with important flag to override any CSS
        htmlCell.style.setProperty('min-height', '19px', 'important');
        htmlCell.style.setProperty('height', '19px', 'important');
        htmlCell.style.setProperty('max-height', '19px', 'important');
        htmlCell.style.setProperty('line-height', '13px', 'important');
        htmlCell.style.setProperty('vertical-align', 'middle', 'important');
        htmlCell.style.setProperty('padding-top', '3px', 'important');
        htmlCell.style.setProperty('padding-bottom', '3px', 'important');
        htmlCell.style.setProperty('padding-left', '4px', 'important');
        htmlCell.style.setProperty('padding-right', '4px', 'important');
        htmlCell.style.setProperty('box-sizing', 'border-box', 'important');
        htmlCell.style.setProperty('display', 'table-cell', 'important');
        htmlCell.style.setProperty('overflow', 'hidden', 'important');
        // Force content height calculation
        if (htmlCell.textContent?.trim() === '' && htmlCell.innerHTML.trim() === '') {
          htmlCell.innerHTML = '&nbsp;';
        }
      });
      
      // Force row height explicitly with important flag
      htmlRow.style.setProperty('display', 'table-row', 'important');
      htmlRow.style.setProperty('min-height', '19px', 'important');
      htmlRow.style.setProperty('height', '19px', 'important');
      htmlRow.style.setProperty('max-height', '19px', 'important');
    });
    
    // Also ensure tables have proper layout
    const allTables = el.querySelectorAll('.construction-roumuhi-table, .construction-main-table');
    allTables.forEach((table: Element) => {
      const htmlTable = table as HTMLElement;
      htmlTable.style.setProperty('table-layout', 'fixed', 'important');
      htmlTable.style.setProperty('border-collapse', 'collapse', 'important');
    });
    
    // Optimize header rows - more compact for single page
    const headerRows = el.querySelectorAll('tr:nth-child(1), tr:nth-child(2)');
    headerRows.forEach((row: Element) => {
      const htmlRow = row as HTMLElement;
      const cells = htmlRow.querySelectorAll('td, th');
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.height = '20px'; // Reduced height
        htmlCell.style.minHeight = '20px';
        htmlCell.style.maxHeight = '20px';
        htmlCell.style.fontSize = '9px'; // Smaller font
        htmlCell.style.fontWeight = 'bold'; // Bold text for better visibility
        htmlCell.style.color = '#000000'; // Ensure pure black text
        htmlCell.style.textRendering = 'optimizeLegibility'; // Better text rendering
      });
    });
    
    // Optimize separator rows - more compact
    const separatorRows = el.querySelectorAll('.construction-separator-row');
    separatorRows.forEach((row: Element) => {
      const htmlRow = row as HTMLElement;
      const cells = htmlRow.querySelectorAll('td');
      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        htmlCell.style.height = '8px'; // Reduced height
        htmlCell.style.minHeight = '8px';
        htmlCell.style.maxHeight = '8px';
        htmlCell.style.padding = '0';
      });
    });
    
  };
  
  // Use the cloned element for PDF generation
  const pdfElement = clonedElement;
  
  /**
   * Applies table layout fixes to ensure consistent rendering in PDF.
   * 
   * Sets table-layout to 'fixed' for predictable column widths and
   * border-collapse to 'collapse' for clean border rendering.
   * 
   * @param el - HTML element containing tables to fix
   * 
   * @private
   */
  const applyTableLayoutFixes = (el: HTMLElement) => {
    /**
     * Ensure roumuhi tables use fixed layout for consistent cell sizing.
     * Fixed layout improves rendering predictability in PDF output.
     */
    const roumohiTables = el.querySelectorAll('.construction-roumuhi-table');
    roumohiTables.forEach((table: Element) => {
      const htmlTable = table as HTMLElement;
      htmlTable.style.setProperty('table-layout', 'fixed', 'important');
      htmlTable.style.setProperty('border-collapse', 'collapse', 'important');
    });
    
    /**
     * Ensure main tables use fixed layout for consistent rendering.
     */
    const mainTables = el.querySelectorAll('.construction-main-table');
    mainTables.forEach((table: Element) => {
      const htmlTable = table as HTMLElement;
      htmlTable.style.setProperty('table-layout', 'fixed', 'important');
      htmlTable.style.setProperty('border-collapse', 'collapse', 'important');
    });
  };
  
  /**
   * Applies construction-specific PDF styling to preserve desktop layout.
   * 
   * Forces column widths and optimizes layout for PDF generation.
   * Ensures consistent column sizing regardless of content.
   * 
   * @param el - HTML element to apply construction-specific styling to
   * 
   * @private
   */
  const applyConstructionPdfStyling = (el: HTMLElement) => {
    /**
     * Force column widths directly via JavaScript for main tables.
     * 
     * Sets fixed column widths to ensure consistent layout in PDF:
     * - First column (section labels): 15px
     * - Other columns: 50px
     * 
     * This prevents column width variations based on content.
     */
    const mainTables = el.querySelectorAll('.construction-main-table');
    mainTables.forEach((table: Element) => {
      const htmlTable = table as HTMLElement;
      // Set table layout to fixed to enforce column widths
      htmlTable.style.tableLayout = 'fixed';
      htmlTable.style.width = 'auto';
      htmlTable.style.minWidth = '100%';
      htmlTable.style.maxWidth = 'none';
      
      /**
       * Force column widths directly on each cell.
       * First column (cellIndex 0) is narrower for vertical section labels.
       */
      const allCells = htmlTable.querySelectorAll('td, th');
      allCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        const isFirstColumn = (htmlCell as HTMLTableCellElement).cellIndex === 0;
        
        if (isFirstColumn) {
          // First column: 15px width (for vertical text section labels)
          htmlCell.style.setProperty('width', '15px', 'important');
          htmlCell.style.setProperty('min-width', '15px', 'important');
          htmlCell.style.setProperty('max-width', '15px', 'important');
        } else {
          // Other columns: 50px width (standard data columns)
          htmlCell.style.setProperty('width', '50px', 'important');
          htmlCell.style.setProperty('min-width', '50px', 'important');
          htmlCell.style.setProperty('max-width', '50px', 'important');
        }
      });
    });
    
    // Apply same logic to roumuhi tables
    const roumuhiTables = el.querySelectorAll('.construction-roumuhi-table');
    roumuhiTables.forEach((table: Element) => {
      const htmlTable = table as HTMLElement;
      htmlTable.style.tableLayout = 'fixed';
      htmlTable.style.width = 'auto';
      htmlTable.style.minWidth = '100%';
      htmlTable.style.maxWidth = 'none';
      
      // Force column widths directly
      const allCells = htmlTable.querySelectorAll('td, th');
      allCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        const isFirstColumn = (htmlCell as HTMLTableCellElement).cellIndex === 0;
        
        if (isFirstColumn) {
          htmlCell.style.setProperty('width', '15px', 'important');
          htmlCell.style.setProperty('min-width', '15px', 'important');
          htmlCell.style.setProperty('max-width', '15px', 'important');
        } else {
          htmlCell.style.setProperty('width', '50px', 'important');
          htmlCell.style.setProperty('min-width', '50px', 'important');
          htmlCell.style.setProperty('max-width', '50px', 'important');
        }
      });
    });
    
    // Make company name bigger in PDF
    const companyNameCells = el.querySelectorAll('.construction-white-bg.construction-no-border');
    companyNameCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      if (htmlCell.textContent?.includes('株式会社')) {
        htmlCell.style.setProperty('font-size', '18px', 'important');
        htmlCell.style.setProperty('font-weight', 'bold', 'important');
      }
    });
    
    // Minimal spacing adjustments - more compact for single page
    const sections = el.querySelectorAll('.construction-section, .construction-roumuhi-section');
    sections.forEach((section: Element) => {
      const htmlSection = section as HTMLElement;
      htmlSection.style.marginBottom = '5px'; // Reduced spacing
    });
  };
  
  applyConstructionPdfStyling(pdfElement);
  applyTableLayoutFixes(pdfElement);
  
  // WYSIWYG capture with slight compaction for PDF sizing
  pdfElement.classList.add('pdf-compact');
  if (!isMobilePdfExport) {
    pdfElement.classList.add('pdf-compact-scale');
  }

  const cleanupAfterPdf = () => {
    if (isMobilePdfExport) {
      document.documentElement.classList.remove('pdf-mobile-export');
    }

    if (originalBorderColor) {
      element.style.setProperty('--payslip-border-color', originalBorderColor);
    } else {
      element.style.removeProperty('--payslip-border-color');
    }
    if (originalBackgroundColor) {
      element.style.setProperty('--payslip-background-color', originalBackgroundColor);
    } else {
      element.style.removeProperty('--payslip-background-color');
    }
    if (originalRoumuhiBorderColor) {
      element.style.setProperty('--payslip-roumuhi-border-color', originalRoumuhiBorderColor);
    } else {
      element.style.removeProperty('--payslip-roumuhi-border-color');
    }
    if (originalRoumuhiBackgroundColor) {
      element.style.setProperty('--payslip-roumuhi-background-color', originalRoumuhiBackgroundColor);
    } else {
      element.style.removeProperty('--payslip-roumuhi-background-color');
    }

    const allTableCells = element.querySelectorAll('td, th');
    allTableCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      htmlCell.style.removeProperty('border-color');
      htmlCell.style.removeProperty('background-color');
      htmlCell.style.removeProperty('border-style');
      htmlCell.style.removeProperty('border-width');
      htmlCell.style.removeProperty('border-left-width');
      htmlCell.style.removeProperty('border-right-width');
      htmlCell.style.removeProperty('border-top-width');
      htmlCell.style.removeProperty('border-bottom-width');
      htmlCell.style.removeProperty('vertical-align');
      htmlCell.style.removeProperty('text-align');
      htmlCell.style.removeProperty('writing-mode');
      htmlCell.style.removeProperty('text-orientation');
      htmlCell.style.removeProperty('transform');
      htmlCell.style.removeProperty('white-space');
      htmlCell.style.removeProperty('overflow');
      htmlCell.style.removeProperty('text-overflow');
      htmlCell.style.removeProperty('min-height');
      htmlCell.style.removeProperty('height');

      if (htmlCell.innerHTML === '&nbsp;') {
        htmlCell.innerHTML = '';
      }
    });

    const tempContainer = (element as any).tempContainer;
    if (tempContainer && tempContainer.parentNode) {
      tempContainer.parentNode.removeChild(tempContainer);
    }

    element.classList.remove('pdf-generation');
    element.classList.remove('pdf-compact');
    element.classList.remove('pdf-compact-scale');
    element.style.removeProperty('width');
    element.style.removeProperty('max-width');
    element.style.removeProperty('max-height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('margin');
    element.style.removeProperty('display');
    element.style.removeProperty('transform');
    element.style.removeProperty('transform-origin');

    const allElements = element.querySelectorAll('*');
    allElements.forEach((elem: Element) => {
      const htmlElem = elem as HTMLElement;
      if (originalBorderColor) {
        htmlElem.style.setProperty('--payslip-border-color', originalBorderColor);
      } else {
        htmlElem.style.removeProperty('--payslip-border-color');
      }
      if (originalBackgroundColor) {
        htmlElem.style.setProperty('--payslip-background-color', originalBackgroundColor);
      } else {
        htmlElem.style.removeProperty('--payslip-background-color');
      }
      if (originalRoumuhiBorderColor) {
        htmlElem.style.setProperty('--payslip-roumuhi-border-color', originalRoumuhiBorderColor);
      } else {
        htmlElem.style.removeProperty('--payslip-roumuhi-border-color');
      }
      if (originalRoumuhiBackgroundColor) {
        htmlElem.style.setProperty('--payslip-roumuhi-background-color', originalRoumuhiBackgroundColor);
      } else {
        htmlElem.style.removeProperty('--payslip-roumuhi-background-color');
      }
    });
  };
  
  /**
   * Generates PDF using html2pdf.js library.
   * 
   * Configures html2canvas and jsPDF with optimal settings for:
   * - High quality rendering (300 DPI)
   * - Proper Japanese font rendering
   * - Single-page A4 landscape format
   * - Sharp borders and text
   * 
   * Uses requestAnimationFrame and setTimeout delays to ensure all styles
   * are applied before PDF capture.
   * 
   * @private
   */
  const applyJapaneseFontOnClone = (clonedDoc: Document) => {
    const japaneseFontStack =
      '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
    clonedDoc.querySelectorAll('*').forEach((elem: Element) => {
      const htmlElem = elem as HTMLElement;
      if (htmlElem.style) {
        htmlElem.style.setProperty('font-family', japaneseFontStack, 'important');
        htmlElem.style.setProperty('unicode-bidi', 'embed', 'important');
        htmlElem.style.setProperty('direction', 'ltr', 'important');
      }
    });
  };

  const generatePdf = () => {
  const period = selectedPeriod ?? '';
  const timeLabel =
    payslipKind === 'bonus'
      ? period.endsWith('月')
        ? `${selectedYear}年${period}`
        : `${selectedYear}年${period}月`
      : `${selectedYear}年${selectedMonth}月`;
  const docKind = payslipKind === 'bonus' ? '建設業賞与明細書' : '建設業給与明細書';
  const filename = `${docKind}_${timeLabel}_${empCode}_${selectedSite}.pdf`;

  const html2canvasBase = {
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    scrollX: 0,
    scrollY: 0,
    letterRendering: true,
    logging: false,
    removeContainer: false,
    foreignObjectRendering: false,
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    onclone: applyJapaneseFontOnClone,
  };

  const opt = {
    margin: isMobilePdfExport ? MOBILE_PDF_MARGIN_MM : ([5, 5, 5, 5] as [number, number, number, number]),
    filename,
    image: { type: 'jpeg' as const, quality: 0.92 },
    pagebreak: isMobilePdfExport
      ? { mode: ['css', 'legacy'] as const }
      : { mode: ['avoid-all', 'css', 'legacy'] as const },
    html2canvas: isMobilePdfExport
      ? {
          ...html2canvasBase,
          scale: 1.5,
          width: MOBILE_PDF_CAPTURE_WIDTH_PX,
          height: MOBILE_PDF_CAPTURE_HEIGHT_PX,
          windowWidth: 1123,
          windowHeight: 794,
          dpi: 200,
          pixelRatio: Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2),
        }
      : {
          ...html2canvasBase,
          scale: 2.0,
          width: pdfElement.scrollWidth,
          height: pdfElement.scrollHeight,
          windowWidth: 1400,
          windowHeight: 900,
          dpi: 300,
          pixelRatio: 2,
        },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'landscape' as const,
      compress: false,
      putOnlyUsedFonts: true,
      floatPrecision: 4,
    },
  };

  type JsPdfDoc = {
    internal: { getNumberOfPages: () => number };
    deletePage: (n: number) => void;
    save: (name: string) => void;
  };

  const runPdfExport = (): Promise<void> => {
    const worker = html2pdf().set(opt).from(pdfElement);
    if (isMobilePdfExport) {
      const mobileWorker = worker as unknown as {
        toPdf: () => { get: (key: 'pdf') => Promise<JsPdfDoc> };
      };
      return mobileWorker
        .toPdf()
        .get('pdf')
        .then((pdf: JsPdfDoc) => {
          const pageCount = pdf.internal.getNumberOfPages();
          if (pageCount > 1) {
            pdf.deletePage(pageCount);
          }
          pdf.save(filename);
        });
    }
    return worker.save().then(() => undefined);
  };

  runPdfExport()
    .then(() => {
    cleanupAfterPdf();
  }).catch((error: unknown) => {
    /**
     * Fallback PDF generation with simplified options.
     * 
     * If the primary PDF generation fails, attempts a simpler approach
     * with reduced options for maximum compatibility.
     */
    const fallbackOpt = {
      margin: isMobilePdfExport ? MOBILE_PDF_MARGIN_MM : ([3, 3, 3, 3] as [number, number, number, number]),
      filename,
      image: { type: 'jpeg' as const, quality: 0.9 },
      html2canvas: isMobilePdfExport
        ? {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: MOBILE_PDF_CAPTURE_WIDTH_PX,
            height: MOBILE_PDF_CAPTURE_HEIGHT_PX,
            windowWidth: 1123,
            windowHeight: 794,
            dpi: 200,
            pixelRatio: Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2),
          }
        : {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: 1200,
            height: 800,
            windowWidth: 1200,
            windowHeight: 800,
            dpi: 300,
            pixelRatio: 2,
          },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'landscape' as const,
        compress: false,
      },
    };

    html2pdf()
      .set(fallbackOpt)
      .from(pdfElement)
      .save()
      .then(() => {
        cleanupAfterPdf();
      })
      .catch((fallbackError) => {
      const errMsg = error instanceof Error ? error.message : String(error);
      const fallbackMsg = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
      alert('PDFの生成に失敗しました。エラー: ' + errMsg + '\nフォールバックも失敗: ' + fallbackMsg);
      cleanupAfterPdf();
    });
  }); // End of first .catch() block
  }; // End of generatePdf function
  
  /**
   * Use requestAnimationFrame to ensure all styles are applied before PDF capture.
   * 
   * Double requestAnimationFrame ensures:
   * 1. Browser has applied all CSS styles
   * 2. DOM layout is complete
   * 3. Heights are calculated correctly
   * 
   * Additional setTimeout ensures browser has fully rendered before capture.
   */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      /**
       * Delay to ensure browser has applied all styles, rendered the DOM,
       * and calculated heights. This is critical for consistent PDF output,
       * especially for dynamically sized elements.
       */
      setTimeout(() => {
        if (isMobilePdfExport) {
          document.documentElement.classList.add('pdf-mobile-export');
        }

        /**
         * Final pass: Re-apply styles one more time just before PDF capture.
         * 
         * This ensures all styles are definitely set, even if browser
         * recalculated layouts between application and capture.
         * 
         * Processes critical sections:
         * - Roumuhi (labor cost) section rows
         * - Koujo (deduction) section rows
         * - Furikomi (transfer) section rows
         */
        const finalRoumohiRows = pdfElement.querySelectorAll('.construction-roumuhi-section');
        finalRoumohiRows.forEach((row: Element) => {
          const htmlRow = row as HTMLElement;
          const cells = htmlRow.querySelectorAll('td, th');
          cells.forEach((cell: Element) => {
            const htmlCell = cell as HTMLElement;
            // Re-enforce row height for consistent PDF output
            htmlCell.style.setProperty('height', '19px', 'important');
            htmlCell.style.setProperty('min-height', '19px', 'important');
            htmlCell.style.setProperty('max-height', '19px', 'important');
            // Force Japanese font to prevent character corruption on mobile devices
            const japaneseFontStackFinal = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
            htmlCell.style.setProperty('font-family', japaneseFontStackFinal, 'important');
            // Ensure empty cells have content for row height
            if (!htmlCell.textContent || htmlCell.textContent.trim() === '') {
              htmlCell.innerHTML = '&nbsp;';
            }
          });
          htmlRow.style.setProperty('height', '19px', 'important');
        });
        
        // Final pass for koujo (deduction) section
        const finalKoujoRows = pdfElement.querySelectorAll('.construction-koujo-section');
        finalKoujoRows.forEach((row: Element) => {
          const htmlRow = row as HTMLElement;
          const cells = htmlRow.querySelectorAll('td, th');
          cells.forEach((cell: Element) => {
            const htmlCell = cell as HTMLElement;
            htmlCell.style.setProperty('height', '19px', 'important');
            htmlCell.style.setProperty('min-height', '19px', 'important');
            htmlCell.style.setProperty('max-height', '19px', 'important');
            // Force Japanese font to prevent character corruption on mobile devices
            const japaneseFontStackFinalKoujo = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
            htmlCell.style.setProperty('font-family', japaneseFontStackFinalKoujo, 'important');
            if (!htmlCell.textContent || htmlCell.textContent.trim() === '') {
              htmlCell.innerHTML = '&nbsp;';
            }
          });
          htmlRow.style.setProperty('height', '19px', 'important');
        });
        
        /**
         * Final pass for furikomi (transfer) section rows.
         * Ensures consistent row height and proper padding.
         */
        const finalFurikomiRows = pdfElement.querySelectorAll('.construction-furikomi-section');
        finalFurikomiRows.forEach((row: Element) => {
          const htmlRow = row as HTMLElement;
          const cells = htmlRow.querySelectorAll('td, th');
          cells.forEach((cell: Element) => {
            const htmlCell = cell as HTMLElement;
            htmlCell.style.setProperty('height', '19px', 'important');
            htmlCell.style.setProperty('min-height', '19px', 'important');
            htmlCell.style.setProperty('max-height', '19px', 'important');
            htmlCell.style.setProperty('line-height', '13px', 'important');
            htmlCell.style.setProperty('padding-top', '3px', 'important');
            htmlCell.style.setProperty('padding-bottom', '3px', 'important');
            htmlCell.style.setProperty('vertical-align', 'middle', 'important');
            // Force Japanese font to prevent character corruption on mobile devices
            const japaneseFontStackFinalFurikomi = '"Yu Gothic", "Hiragino Sans", "Noto Sans JP", "Source Han Sans JP", "MS Gothic", "Meiryo", "MS PGothic", "MS PMincho", sans-serif';
            htmlCell.style.setProperty('font-family', japaneseFontStackFinalFurikomi, 'important');
            if (!htmlCell.textContent || htmlCell.textContent.trim() === '') {
              htmlCell.innerHTML = '&nbsp;';
            }
          });
          htmlRow.style.setProperty('height', '19px', 'important');
        });
        
        // Generate PDF after all styles are confirmed to be applied
        generatePdf();
      }, 200); // 200ms delay for better reliability - ensures browser has fully rendered
    });
  });
};
